import { navigateTo, useAsyncData, useRuntimeConfig } from '#imports'
import { computed, ref, watch } from 'vue'

import type { Bcp47Locale } from '@backmarket/http-api/src/standards/Locale'
import { useCareLogger } from '@backmarket/nuxt-layer-care-platform/useCareLogger'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useDebouncedRef } from '@backmarket/utils/composables/useDebouncedRef'

import {
  DEBOUNCE_DELAY,
  MAX_OPTIONS,
  MIN_SEARCH_LENGTH,
} from './FAQSection.constants'
import translations from './FAQSection.translations'

const HELP_CENTER_FALLBACK_LOCALES: Partial<Record<Bcp47Locale, Bcp47Locale>> =
  {
    'es-us': 'en-us',
  }

export function useFAQArticleSearch() {
  const i18n = useI18n()
  const locale = useI18nLocale()
  const {
    FF_ENABLE_NEW_HELP_CENTER_DISPLAY_ARTICLES_SEARCH: displayArticlesSearch,
  } = useRuntimeConfig().public
  const search = ref('')
  const $httpFetch = useHttpFetch()
  const { logHttpError } = useCareLogger()

  const debouncedSearch = useDebouncedRef(search, DEBOUNCE_DELAY)

  let abortController: AbortController | undefined

  const { data, error } = useAsyncData(
    async () => {
      if (abortController) {
        abortController.abort('New request')
      }

      if (
        !displayArticlesSearch ||
        debouncedSearch.value.trim().length < MIN_SEARCH_LENGTH
      ) {
        return []
      }

      const abortCtrl = new AbortController()
      abortController = abortCtrl

      return $httpFetch(
        '/api/care-platform/external/v1/help-center/articles/search',
        {
          query: {
            query: debouncedSearch.value,
          },
          signal: abortCtrl.signal,
        },
      )
    },
    { watch: [debouncedSearch], immediate: false, lazy: true },
  )

  watch(error, () => {
    if (!error.value) return

    logHttpError({
      error: error.value,
      errorStatusCode: error.value.status ?? 0,
      errorDetail: { scope: 'help_center' },
      errorTitle: '[HELP CENTER] Error when searching for FAQ articles',
    })
  })

  const options = computed<{ label: string; url?: string }[]>(() => {
    if (!Array.isArray(data.value)) {
      return []
    }

    return [
      ...data.value.slice(0, MAX_OPTIONS).map((article) => ({
        label: article.name,
        url: article.url,
      })),
      {
        label: i18n(translations.seeMoreResult),
        url: `https://help.backmarket.com/${HELP_CENTER_FALLBACK_LOCALES[locale] ?? locale}`,
      },
    ]
  })

  function handleSelectedItem(selectedItem: { url?: string }) {
    if (selectedItem.url) {
      navigateTo(selectedItem.url, { external: true })
    }
  }

  return { search, options, handleSelectedItem, displayArticlesSearch }
}
